import { Component, computed, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ContactPointType } from '../../../api/app-types/contact-point.app';
import { SponsorExtended, SponsorPerson } from '../../../api/app-types/sponsor.app';
import { LvrRedirectNav } from '../../../core/classes/redirect-nav.class';
import { LvrHeading } from '../../../core/components/heading/heading';
import { CarouselElement, NavCarousel } from '../../../core/components/nav-carousel/nav-carousel';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { GenericDeleteDialog } from '../../../core/components/generic-delete-dialog/generic-delete-dialog';
import { SPONSOR_SERVICE } from '../../../api/services/sponsor.service';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { NeighborContext } from '../../../api/app-types/neighbors.app';

@Component({
	selector: 'lvr-sponsor-person-details',
	imports: [LvrHeading, NavCarousel, TranslocoPipe],
	templateUrl: './sponsor-person-details.html',
	styleUrl: './sponsor-person-details.scss',
})
export class SponsorPersonDetails extends LvrRedirectNav {
	private readonly personNeighbors = this.resolveDataAsSignal<NeighborContext<SponsorPerson>>('personNeighbors');
	private readonly sponsor = this.resolveDataAsSignal<SponsorExtended>('sponsor');
	private readonly sponsorId = computed(() => this.sponsor()?.id);
	public readonly person = this.resolveDataAsSignal<SponsorPerson>('person');
	private readonly matDialog = inject(MatDialog);
	private readonly api = inject(SPONSOR_SERVICE);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	public readonly disableDeleteTranslationKey = 'sponsor.persons.details.deactivate_disable';
	public readonly canEditSponsorPersons = hasPermission(this.session.permissions, 'InstitutionPersons.Edit');
	public readonly canDeleteSponsorPersons = hasPermission(this.session.permissions, 'InstitutionPersons.Delete');

	public readonly personName = computed(() =>
		this.person()?.firstName
			? `${this.person()?.name ?? ''}, ${this.person()?.firstName ?? ''}`.trim()
			: `${this.person()?.name ?? ''}`.trim(),
	);
	public readonly backLink = computed(() => `/desk/sponsor/${this.sponsorId()}/persons`);

	public readonly preferredCommunicationPoints = computed(
		() => this.person()?.contactPoints.filter((cp) => cp.preferredWayOfContact) ?? [],
	);

	public readonly otherCommunicationPoints = computed(
		() => this.person()?.contactPoints.filter((cp) => !cp.preferredWayOfContact) ?? [],
	);

	public readonly contactTypes = {
		phone: ContactPointType.PHONE,
		email: ContactPointType.EMAIL,
	};

	public readonly carouselElements = computed(() => {
		const neighbors = this.personNeighbors();

		if (!neighbors) return [];

		return [neighbors.previous, neighbors.current, neighbors.next]
			.filter((person): person is SponsorPerson => !!person && !person.isDeactivated)
			.map((person) => ({
				id: person.id.toString(),
				label: person.firstName
					? `${person.name ?? ''}, ${person.firstName ?? ''}`.trim()
					: `${person.name ?? ''}`.trim(),
			}));
	});

	public readonly currentIndex = computed(() => {
		const person = this.person();
		const elements = this.carouselElements();

		if (!person || elements.length === 0) return 0;

		const index = elements.findIndex((e) => e.id === person.id.toString());

		return index >= 0 ? index : 0;
	});

	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.navigateAfterFinished(['../', event.element.id], { relativeTo: this.route });
	}

	public openEdit() {
		if (!this.canEditSponsorPersons()) return;
		this.navigateAfterFinished(['edit'], { relativeTo: this.route });
	}

	public async openDialog() {
		if (!this.canDeleteSponsorPersons()) return;
		const person = this.person();
		const sponsor = this.sponsor();
		if (!person || !sponsor) return;

		const confirmed = await lastValueFrom(
			this.matDialog
				.open(GenericDeleteDialog, {
					width: '600px',
					data: {
						titleKey: 'sponsor.persons.deactivate.title',
						messageKey: 'sponsor.persons.deactivate.confirmation',
						messageParams: { firstName: person.firstName, lastName: person.name, sponsorName: sponsor.name },
						confirmKey: 'sponsor.persons.deactivate.deactivate',
						cancelKey: 'sponsor.persons.deactivate.cancel',
						confirmInputText: person.name,
						confirmInputLabelKey: 'sponsor.persons.deactivate.double_check',
						confirmInputPlaceholderKey: 'sponsor.persons.deactivate.insert_name',
						confirmInputPlaceholderParams: { lastName: person.name },
						loadingLabel: 'base.button.deactivating',
						onConfirm: async () => {
							await this.api.deactivatePerson(sponsor.id, person.id);
							await this.navigateAfterFinished(['../'], { relativeTo: this.route });
						},
						onError: () =>
							this.ntfySrv.addNotification(
								createErrorNotification(
									this.transloco.translate('base.notification_message.sponsor_person_deactivation_error'),
								),
							),
					},
				})
				.afterClosed(),
		);

		if (!confirmed) {
			return;
		}

		this.ntfySrv.addNotification(
			createSuccessNotification(
				this.transloco.translate('base.notification_message.sponsor_person_deactivated', {
					firstName: person.firstName,
					lastName: person.name,
				}),
			),
		);
	}
}
